/**
 * Utility for building address API payloads with consistent formatting
 * Shared by AddressEditor and AddressSaver components
 */
// eslint-disable-next-line import/prefer-default-export
export class AddressPayloadBuilder {
  /**
   * Builds address payload for API submission
   *
   * @param {Object} addressData - Address data fields
   * @param {string} addressData.address1 - Street address line 1
   * @param {string} [addressData.address2] - Street address line 2
   * @param {string} addressData.city - City
   * @param {string} [addressData.stateName] - State name
   * @param {string} addressData.zipcode - ZIP code
   * @param {Object} [options={}] - Optional fields
   * @param {string} [options.phone] - Phone number
   * @param {string} [options.fullName] - Full name
   * @param {string} [options.authToken] - 2FA verification token
   * @param {number} [options.userId] - User ID
   * @param {string} [options.orderNumber] - Order number for guest users
   * @param {boolean} [options.customAddress] - Custom address flag
   * @returns {Object} Formatted payload for API
   */
  static buildPayload(addressData, options = {}) {
    if (!addressData || typeof addressData !== "object") {
      throw new Error("Address data is required");
    }

    // Build base payload with required fields
    const payload = {
      address1: this.#trimOrEmpty(addressData.address1),
      address2: this.#trimOrEmpty(addressData.address2),
      city: this.#trimOrEmpty(addressData.city),
      zipcode: this.#trimOrEmpty(addressData.zipcode),
      local_delivery_address: true,
      smarty_autosuggested_address: true
    };

    // Add state if provided
    if (addressData.stateName) {
      payload.state_name = this.#trimOrEmpty(addressData.stateName);
    }

    // Add optional fields
    if (options.phone) {
      const trimmedPhone = String(options.phone).trim();
      payload.phone = trimmedPhone || null;
    }

    if (options.fullName) {
      const trimmedName = String(options.fullName).trim();
      payload.full_name = trimmedName || null;
    }

    if (options.authToken) {
      payload.verification_token = options.authToken;
    }

    if (options.userId) {
      payload.user_id = options.userId;
    }

    if (options.orderNumber) {
      payload.order_number = String(options.orderNumber);
    }

    if (options.customAddress !== undefined) {
      payload.custom_address = options.customAddress;
    }

    return payload;
  }

  /**
   * Trims string value or returns empty string
   * @private
   */
  static #trimOrEmpty(value) {
    if (!value) return "";
    return String(value).trim();
  }
}
